import { useRef, useState, useEffect } from 'react';
import screenfull from 'screenfull';
import { Box, IconButton } from '@mui/material';
import { CloseFullscreenRounded, ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import Image from 'next/image';
import FullscreenIcon2 from '../Icon/FullscreenIcon2';

export default function FloorPlanGallery({ slides }) {
  const containerRef = useRef(null);
  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(screenfull.isFullscreen);
    if (screenfull.isEnabled) {
      screenfull.on('change', handleChange);
      return () => screenfull.off('change', handleChange);
    }
  }, []);

  useEffect(() => {
    const calcVisible = () => {
      if (thumbContainerRef.current) {
        const containerWidth = thumbContainerRef.current.offsetWidth;
        const thumbWidth = window.innerWidth >= 900 ? 118 : 68;
        const count = Math.floor(containerWidth / thumbWidth);
        setVisibleCount(Math.max(1, count));
      }
    };

    const observer = new ResizeObserver(calcVisible);
    const el = thumbContainerRef.current;
    if (el) observer.observe(el);
    calcVisible();

    return () => {
      if (el) observer.unobserve(el);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (thumbStart + visibleCount > slides.length) {
      setThumbStart(Math.max(0, slides.length - visibleCount));
    }
  }, [visibleCount, slides.length]);

  const handleEnterFullscreen = () => {
    if (screenfull.isEnabled && containerRef.current) {
      screenfull.request(containerRef.current);
    }
  };

  const handleExitFullscreen = () => {
    if (screenfull.isEnabled && screenfull.isFullscreen) {
      screenfull.exit();
    }
  };

  const handlePrevThumb = () => {
    setThumbStart((prev) => Math.max(0, prev - 1));
  };

  const handleNextThumb = () => {
    setThumbStart((prev) => Math.min(slides.length - visibleCount, prev + 1));
  };

  const visibleThumbnails = slides.slice(thumbStart, thumbStart + visibleCount);

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid #E0E0E0',
        background: '#fff',
        p: 2,
      }}
    >
      {/* Main image */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: 0,
          paddingBottom: '75%',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {slides[currentIndex]?.src && (
          <Box sx={{ position: 'absolute', top: 45, left: 0, right: 0, bottom: 45 }}>
            <Image
              src={slides[currentIndex]?.src || ''}
              alt={slides[currentIndex]?.alt || 'Image'}
              fill
              style={{ objectFit: 'contain' }}
            />
          </Box>
        )}

        {!isFullscreen && (
          <IconButton
            onClick={handleEnterFullscreen}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              '&:hover': {
                color: (theme) => theme.palette.primary.main,
                backgroundColor: 'transparent',
              },
            }}
          >
            <FullscreenIcon2 />
          </IconButton>
        )}

        {isFullscreen && (
          <IconButton
            onClick={handleExitFullscreen}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              color: '#fff',
              '&:hover': {
                color: (theme) => theme.palette.primary.main,
              },
            }}
          >
            <CloseFullscreenRounded />
          </IconButton>
        )}
      </Box>

      {/* Thumbnail bar */}
      {slides.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          {thumbStart > 0 && slides.length > visibleCount && (
            <IconButton onClick={handlePrevThumb}>
              <ArrowBackIos fontSize="small" />
            </IconButton>
          )}

          <Box ref={thumbContainerRef} sx={{ display: 'flex', gap: 1, overflow: 'hidden', flexGrow: 1 }}>
            {visibleThumbnails.map((thumb, i) => {
              const realIndex = thumbStart + i;
              return (
                <Box
                  key={realIndex}
                  onClick={() => setCurrentIndex(realIndex)}
                  sx={{
                    width: { xs: 60, md: 100 },
                    height: { xs: 60, md: 100 },
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    p: 1,
                    border: (theme) =>
                      realIndex === currentIndex ? `2px solid ${theme.palette.primary.main}` : '1px solid #ccc',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ position: 'relative', width: '100%', height: '100%', p: 1 }}>
                    <Image
                      src={thumb?.thumbnail || thumb?.src}
                      alt={`Thumbnail ${realIndex + 1}`}
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  </Box>

                  {realIndex !== currentIndex && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#FFFFFF99',
                        zIndex: 1,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {thumbStart + visibleCount < slides.length && (
            <IconButton onClick={handleNextThumb}>
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}
